import React from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Tag,
  Tooltip,
  Avatar,
  theme,
  App,
} from "antd";
import {
  DesktopOutlined,
  MobileOutlined,
  TabletOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import type { ISession } from "src/hooks/useSessions";
import { useLanguage } from "src/providers/LanguageProvider";
import api from "src/services/api";

const { Text } = Typography;

function safeDate(iso?: string | null) {
  const dt = iso ? new Date(iso) : null;
  return dt && !Number.isNaN(dt.getTime()) ? dt : null;
}
function formatDate(iso?: string | null, locale = "tr-TR") {
  const dt = safeDate(iso);
  if (!dt) return "-";
  return dt.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
function relativeTimeFromNow(iso?: string | null, locale = "tr-TR") {
  const dt = safeDate(iso);
  if (!dt) return "-";
  const diffMs = dt.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  const sec = Math.round(abs / 1000);
  if (sec < 60) return rtf.format(Math.sign(diffMs) * -sec, "second");
  const min = Math.round(sec / 60);
  if (min < 60) return rtf.format(Math.sign(diffMs) * -min, "minute");
  const hr = Math.round(min / 60);
  if (hr < 24) return rtf.format(Math.sign(diffMs) * -hr, "hour");
  const day = Math.round(hr / 24);
  if (day < 30) return rtf.format(Math.sign(diffMs) * -day, "day");
  const mon = Math.round(day / 30);
  if (mon < 12) return rtf.format(Math.sign(diffMs) * -mon, "month");
  const yr = Math.round(mon / 12);
  return rtf.format(Math.sign(diffMs) * -yr, "year");
}

function getDeviceIcon(platform?: string, ua?: string) {
  const hint = `${platform || ""} ${ua || ""}`.toLowerCase();
  if (/ipad|tablet/.test(hint)) return <TabletOutlined />;
  if (/iphone|ipod|android.*mobile|mobile/.test(hint)) return <MobileOutlined />;
  if (/android/.test(hint) && !/mobile/.test(hint)) return <TabletOutlined />;
  return <DesktopOutlined />;
}
function getDeviceColor(platform?: string, ua?: string) {
  const hint = `${platform || ""} ${ua || ""}`.toLowerCase();
  if (/ipad|tablet/.test(hint)) return "#1890ff";
  if (/iphone|ipod|android.*mobile|mobile/.test(hint)) return "#52c41a";
  if (/android/.test(hint) && !/mobile/.test(hint)) return "#1890ff";
  return "#722ed1";
}

type Props = {
  session: ISession;
  isCurrent?: boolean;
  onLogout?: (id: string | number) => void;
};

const SessionItem: React.FC<Props> = ({ session: s, isCurrent, onLogout }) => {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const lastSeen = s.LastActivityAt || s.UpdatedAt || s.CreatedAt || null;
  const location = [s.City, s.Country].filter(Boolean).join(", ");
  const uaTail =
    s.UserAgent?.includes(") ") ? s.UserAgent.split(") ").pop() : s.UserAgent || "";

  const [loadingLogoutSession, setLoadingLogoutSession] = React.useState(false);
  const message = App.useApp().message;
  const onClickHandlerLogout = async (id) => {
    setLoadingLogoutSession(true);
    try {
      await api.get(`/account-sessions/logout-session`, {
        params: {
          sessionId: id
        }
      })
      message.success(t("LOGOUT_SESSION_SUCCESS", "Oturumdan çıkış yapıldı"));
      onLogout?.(id);
    } catch (e) {
      message.error(e?.message || t("ERROR_GENERIC", "Bir hata oluştu"));
    } finally {
      setLoadingLogoutSession(false);
    }

  }


  return (
    <Card
      size="small"
      key={String(s.Id)}
      style={{
        borderRadius: 12,
        border: `1px solid ${token.colorSplit}`,   // her zaman aynı
        background: token.colorBgContainer,        // yeşil değil
        marginBottom: 12,
      }}
      bodyStyle={{ padding: 14 }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <Avatar
          size={42}
          icon={getDeviceIcon(s.Platform, s.UserAgent)}
          style={{ backgroundColor: getDeviceColor(s.Platform, s.UserAgent) }}
          shape="square"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* üst satır */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Text strong ellipsis style={{ fontSize: 15, maxWidth: "100%" }}>
              {s.Device || s.Platform || t("UNKNOWN_DEVICE", "Bilinmeyen Cihaz")}
            </Text>
            {isCurrent && (
              <Tag
                color="default"
                style={{
                  marginInlineStart: 0,
                  fontSize: 11,
                  lineHeight: 1.2,
                  padding: "2px 6px",
                }}
              >
                {t("THIS_DEVICE", "Bu cihaz")}
              </Tag>
            )}
          </div>

          <Text type="secondary" style={{ display: "block" }}>
            {s.Platform || t("UNKNOWN_PLATFORM", "Bilinmeyen Platform")}
            {uaTail ? ` • ${uaTail}` : ""}
          </Text>

          {/* alt satır: meta */}
          <Space size={16} wrap style={{ marginTop: 8 }}>
            <Space size={6}>
              <GlobalOutlined style={{ color: token.colorTextTertiary }} />
              <Text type="secondary">
                {s.IpAddress || t("UNKNOWN_IP", "IP adresi yok")}
              </Text>
            </Space>

            {(s.City || s.Country) && (
              <Space size={6}>
                <EnvironmentOutlined style={{ color: token.colorTextTertiary }} />
                <Text type="secondary">
                  {location || t("UNKNOWN_LOCATION", "Konum bilinmiyor")}
                </Text>
              </Space>
            )}

            <Space size={6}>
              <ClockCircleOutlined style={{ color: token.colorTextTertiary }} />
              <Tooltip
                title={`${t("FIRST_SEEN", "İlk görüldü")}: ${formatDate(s.CreatedAt)}`}
              >
                <Text type="secondary">
                  {relativeTimeFromNow(lastSeen)} • {formatDate(lastSeen)}
                </Text>
              </Tooltip>
            </Space>
          </Space>

          {/* küçük etiketler */}
          <Space size={6} wrap style={{ marginTop: 8 }}>
            {s.Connection && <Tag bordered={false}>{s.Connection}</Tag>}
            {s.Screen && <Tag bordered={false}>{s.Screen}</Tag>}
            {s.Language && <Tag bordered={false}>{s.Language}</Tag>}
            {s.Timezone && <Tag bordered={false}>{s.Timezone}</Tag>}
          </Space>
        </div>

        {/* aksiyon */}
        <div style={{ alignSelf: "center" }}>
          <Button
            type="text"
            danger
            size="small"
            icon={<LogoutOutlined />}
            onClick={() => onClickHandlerLogout(s.Id)}
            disabled={isCurrent}
            loading={loadingLogoutSession}
          >
            {isCurrent ? t("THIS_DEVICE", "Bu cihaz") : t("BTN_LOGOUT", "Çıkış Yap")}
          </Button>
        </div>
      </div>
    </Card>

  );
};

export default SessionItem;
